import type { CalendarState } from '@react-stately/calendar';
import { useMemo } from 'react';

import { useDatePickerConfig } from '../../contexts/ConfigContext';
import { isMonthDisabled } from '../../utils';
import { GridPicker } from '../GridPicker';

interface MonthPickerProps {
  state: CalendarState;
  onMonthSelect: (monthIndex: number) => void;
  locale?: string;
}

export function MonthView({ state, onMonthSelect, locale }: MonthPickerProps) {
  const { disablePresets } = useDatePickerConfig();
  const currentMonth = state.focusedDate.month;
  const currentYear = state.focusedDate.year;
  const monthNumbers = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  const monthNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => formatter.format(new Date(currentYear, i, 1)));
  }, [locale, currentYear]);

  const checkMonthDisabled = useMemo(
    () => (month: number) => isMonthDisabled(month, currentYear, state, disablePresets),
    [currentYear, state, disablePresets],
  );

  return (
    <GridPicker
      items={monthNumbers}
      selectedValue={state.value?.month}
      focusedValue={currentMonth}
      onSelect={onMonthSelect}
      renderItem={(monthNumber) => monthNames[monthNumber - 1]}
      dataAttribute="data-month"
      wrap={true}
      ariaLabel="Select month"
      isItemDisabled={checkMonthDisabled}
    />
  );
}
